import { Modal } from 'antd'
import { useCallback, useEffect, useState } from 'react'

import { Button } from 'common/components/Button/Button'
import { BUTTON_MODIFIER, BUTTON_PROPORTION } from 'common/constants/buttonConstants'

import FailIcon from '../assets/fail.svg?react'
import SuccessIcon from '../assets/success.svg?react'

import type { PaymentResult } from './payment-result.utils'
import { ReceiptViewDialog } from './receipt-view-dialog'
import { useReceiptPdf } from './use-receipt-pdf'
import { printReceiptDocument } from './use-receipt-print'

import styles from './payment-result-dialog.module.scss'

type Props = {
  open: boolean
  result: PaymentResult | null
  onClose: () => void
}

export const PaymentResultDialog = ({ open, result, onClose }: Props) => {
  const [isReceiptOpen, setIsReceiptOpen] = useState(false)
  const { printReceipt: downloadReceipt, resetPrintState, isPrinting, printError } = useReceiptPdf()

  useEffect(() => {
    if (!open) {
      setIsReceiptOpen(false)
      resetPrintState()
    }
  }, [open, resetPrintState])

  const receiptHtml = result?.kind === 'success' ? result.receiptHtml : null
  const receiptId = result?.kind === 'success' ? result.receiptId : null

  const handlePrint = useCallback(() => {
    if (!receiptHtml) return
    void printReceiptDocument(receiptHtml)
  }, [receiptHtml])

  const handleDownload = useCallback(() => {
    if (!receiptHtml) return
    void downloadReceipt(receiptHtml, receiptId)
  }, [downloadReceipt, receiptHtml, receiptId])

  const handleOpenReceipt = useCallback(() => setIsReceiptOpen(true), [])
  const handleCloseReceipt = useCallback(() => setIsReceiptOpen(false), [])

  const renderContent = () => {
    if (!result) return null
    if (result.kind === 'error') {
      return (
        <div className={styles.container}>
          <div className={styles.iconWrapper}>
            <FailIcon width={56} height={56} />
          </div>
          <div className={styles.textGroup}>
            <h3 className={styles.title}>Payment failed</h3>
            <p className={styles.subtitle}>{result.message}</p>
          </div>
          <div className={styles.footer}>
            <Button
              modifier={BUTTON_MODIFIER.SECONDARY}
              proportion={BUTTON_PROPORTION.LARGE}
              onClick={onClose}
              className={styles.actionButton}>
              Close
            </Button>
          </div>
        </div>
      )
    }
    return (
      <div className={styles.container}>
        <div className={styles.iconWrapper}>
          <SuccessIcon width={56} height={56} />
        </div>
        <div className={styles.textGroup}>
          <h3 className={styles.title}>Payment Saved!</h3>
          {receiptId !== null && <p className={styles.subtitle}>Receipt #{receiptId}</p>}
        </div>
        {printError && !isReceiptOpen && <p className={styles.printError}>{printError}</p>}
        <div className={styles.footer}>
          {receiptHtml && (
            <Button
              proportion={BUTTON_PROPORTION.LARGE}
              onClick={handleOpenReceipt}
              className={`${styles.actionButton} ${styles.viewButton}`}>
              View Receipt
            </Button>
          )}
          {receiptHtml && (
            <Button
              modifier={BUTTON_MODIFIER.SECONDARY}
              proportion={BUTTON_PROPORTION.LARGE}
              onClick={handlePrint}
              className={styles.actionButton}>
              Print Receipt
            </Button>
          )}
          <Button
            proportion={BUTTON_PROPORTION.LARGE}
            onClick={onClose}
            className={`${styles.actionButton} ${styles.exitButton}`}>
            Exit
          </Button>
        </div>
      </div>
    )
  }

  return (
    <>
      <Modal
        open={open}
        footer={null}
        closable={false}
        maskClosable={false}
        keyboard={false}
        destroyOnHidden
        centered
        width={420}
        classNames={{ content: styles.modalContent }}>
        {renderContent()}
      </Modal>
      {receiptHtml && (
        <ReceiptViewDialog
          open={isReceiptOpen}
          receiptHtml={receiptHtml}
          onClose={handleCloseReceipt}
          onPrint={handlePrint}
          onDownload={handleDownload}
          isDownloading={isPrinting}
          downloadError={printError}
        />
      )}
    </>
  )
}
